// Onboarding.js — Premium cinematic redesign
import { useState, useEffect } from "react";
import SummitSubmark from "./Summit-submark-white.png";

const BACKEND = "https://sqastudybuddy-production.up.railway.app";

const SQA_SUBJECTS = [
  "Biology", "Business Management", "Chemistry", "Computing Science",
  "Design and Manufacture", "Drama", "Economics", "English",
  "Engineering Science", "French", "Geography", "German", "Graphic Communication",
  "History", "Home Economics", "Human Biology", "Latin", "Mathematics",
  "Applications of Mathematics", "Modern Studies", "Music", "Philosophy",
  "Physical Education", "Physics", "Psychology",
  "Religious, Moral and Philosophical Studies", "Sociology", "Spanish",
  "Art and Design", "Administration and IT"
];

const LEVELS = ["National 5", "Higher", "Advanced Higher"];

const GRADE_TARGETS = ["A", "B", "C", "Just pass", "Top of my year"];
const STUDY_HOURS = ["1–2 hrs", "3–4 hrs", "5–6 hrs", "7+ hrs"];
const STRUGGLES = [
  "Running out of time in exams",
  "Remembering key facts",
  "Extended / essay questions",
  "Understanding the command words",
  "Staying consistent with revision",
  "Past paper practice",
];

// ── Slide transition wrapper ─────────────────────────────────────────
function SlideStep({ children, direction, active }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const translateX = mounted ? "0%" : direction === "forward" ? "60px" : "-60px";
  const opacity = mounted ? 1 : 0;

  return (
    <div style={{
      transform: `translateX(${translateX})`,
      opacity,
      transition: "transform 0.45s cubic-bezier(0.16,1,0.3,1), opacity 0.35s ease",
      width: "100%"
    }}>
      {children}
    </div>
  );
}

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0); // 0 = cinematic intro
  const [direction, setDirection] = useState("forward");
  const [stepKey, setStepKey] = useState(0);

  // Form state
  const [name, setName] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState({});
  const [examDate, setExamDate] = useState("");
  const [confidence, setConfidence] = useState({});
  const [gradeTargets, setGradeTargets] = useState({}); // per subject
  const [studyHours, setStudyHours] = useState("");
  const [biggestStruggle, setBiggestStruggle] = useState("");
  const [worry, setWorry] = useState("");
  const [aiReply, setAiReply] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [chosenFreeSubject, setChosenFreeSubject] = useState(null);

  // Intro animation
  const [introStage, setIntroStage] = useState(0); // 0→logo, 1→tagline, 2→button

  useEffect(() => {
    if (step !== 0) return;
    const t1 = setTimeout(() => setIntroStage(1), 600);
    const t2 = setTimeout(() => setIntroStage(2), 1400);
    const t3 = setTimeout(() => setIntroStage(3), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [step]);

  const goTo = (nextStep, dir = "forward") => {
    setDirection(dir);
    setStepKey(k => k + 1);
    setStep(nextStep);
  };

  const goNext = (next) => goTo(next, "forward");
  const goBack = (prev) => goTo(prev, "back");

  const daysUntil = examDate
    ? Math.ceil((new Date(examDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const allLevelsSet = subjects.length > 0 && subjects.every(s => levels[s]);
  const allConfSet = subjects.every(s => confidence[s]);

  const askAI = async () => {
    if (!worry.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${BACKEND}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 200,
          system: `You are Summit, a warm SQA study tutor. A student named ${name || "a student"} just told you their biggest exam worry. Respond in 2–3 sentences — be specific, practical, and genuinely reassuring. Don't be generic. Their subjects: ${subjects.map(s => `${levels[s]} ${s} (target: ${gradeTargets[s] || "not set"})`).join(", ")}. First exam in ${daysUntil} days.`,
          messages: [{ role: "user", content: worry }]
        })
      });
      const data = await res.json();
      setAiReply(data.content?.[0]?.text || "You've got this. Let's get to work.");
    } catch {
      setAiReply("You've got this. Let's get to work.");
    }
    setAiLoading(false);
  };

  const finishFree = () => {
    const picked = chosenFreeSubject || subjects[0];
    onComplete({
      subjects: [picked],
      all_subjects: subjects,
      levels, examDate, confidence,
      worry, plan: "free", display_name: name,
      grade_target: JSON.stringify(gradeTargets), study_hours_per_week: studyHours,
      biggest_struggle: biggestStruggle
    }, false);
  };

  const finishPro = () => {
    onComplete({
      subjects, levels, examDate, confidence,
      worry, plan: "free", display_name: name,
      grade_target: JSON.stringify(gradeTargets), study_hours_per_week: studyHours,
      biggest_struggle: biggestStruggle
    }, true);
  };

  const finishBoost = () => {
    onComplete({
      subjects, levels, examDate, confidence,
      worry, plan: "free", display_name: name,
      grade_target: JSON.stringify(gradeTargets), study_hours_per_week: studyHours,
      biggest_struggle: biggestStruggle
    }, true, "boost");
  };

  // Total steps for progress: 1–8
  const totalSteps = 8;
  const progressPct = step === 0 ? 0 : Math.round((Math.min(step, totalSteps) / totalSteps) * 100);

  const greeting = name ? name.split(" ")[0] : "";

  return (
    <div style={{
      minHeight: "100vh",
      background: "#070d0c",
      color: "#f0f4f2",
      fontFamily: "'DM Sans', sans-serif",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital,wght@0,400;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        * { box-sizing: border-box; }

        input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(1) opacity(0.4); }
        input:focus { outline: none; }
        textarea:focus { outline: none; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.85); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-8px); }
        }
        @keyframes glow {
          0%, 100% { opacity: 0.3; }
          50%       { opacity: 0.7; }
        }

        .gradient-text {
          background: linear-gradient(135deg, #1D9E75, #4DC9A0, #7c3aed, #1D9E75);
          background-size: 300% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 5s linear infinite;
        }

        .subject-chip {
          padding: 8px 16px;
          border-radius: 24px;
          font-size: 13px;
          border: 1px solid rgba(255,255,255,0.1);
          background: transparent;
          color: rgba(240,244,242,0.5);
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.2s cubic-bezier(0.16,1,0.3,1);
        }
        .subject-chip:hover {
          border-color: rgba(29,158,117,0.4);
          color: rgba(240,244,242,0.8);
          background: rgba(29,158,117,0.06);
        }
        .subject-chip.active {
          border-color: #1D9E75;
          background: rgba(29,158,117,0.15);
          color: #4DC9A0;
        }

        .btn-primary {
          width: 100%;
          padding: 16px;
          background: #1D9E75;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          letter-spacing: -0.2px;
        }
        .btn-primary:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(29,158,117,0.3); }
        .btn-primary:disabled {
          background: rgba(255,255,255,0.06);
          color: rgba(240,244,242,0.2);
          cursor: default;
          transform: none;
          box-shadow: none;
        }

        .btn-back {
          padding: 16px 22px;
          background: transparent;
          color: rgba(240,244,242,0.35);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          font-size: 15px;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-back:hover { color: rgba(240,244,242,0.6); border-color: rgba(255,255,255,0.2); }

        .fancy-input {
          width: 100%;
          padding: 18px 20px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: #f0f4f2;
          font-size: 18px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 400;
          transition: all 0.2s;
        }
        .fancy-input:focus {
          border-color: rgba(29,158,117,0.5);
          background: rgba(29,158,117,0.04);
          box-shadow: 0 0 0 3px rgba(29,158,117,0.08);
        }
        .fancy-input::placeholder { color: rgba(240,244,242,0.2); }

        .plan-card {
          padding: 28px 24px;
          border-radius: 18px;
          display: flex;
          flex-direction: column;
          transition: all 0.3s cubic-bezier(0.16,1,0.3,1);
          position: relative;
        }
        .plan-card:hover { transform: translateY(-4px); }

        .original-price {
          position: relative;
          color: rgba(240,244,242,0.3);
        }
        .original-price::after {
          content: '';
          position: absolute;
          left: 0; top: 50%;
          height: 1.5px; width: 100%;
          background: rgba(226,75,74,0.7);
          transform: rotate(-6deg);
        }

        .step-label {
          font-size: 11px;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #1D9E75;
          font-weight: 500;
          margin-bottom: 12px;
        }

        .step-heading {
          font-family: 'Instrument Serif', serif;
          font-size: clamp(28px, 5vw, 42px);
          letter-spacing: -1px;
          color: #f0f4f2;
          line-height: 1.1;
          margin-bottom: 12px;
        }

        .step-sub {
          font-size: 15px;
          color: rgba(240,244,242,0.4);
          font-weight: 300;
          line-height: 1.65;
          margin-bottom: 36px;
        }

        .option-btn {
          width: 100%;
          padding: 14px 18px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.02);
          color: rgba(240,244,242,0.55);
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          cursor: pointer;
          text-align: left;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .option-btn:hover {
          border-color: rgba(29,158,117,0.3);
          background: rgba(29,158,117,0.04);
          color: rgba(240,244,242,0.8);
        }
        .option-btn.active {
          border-color: #1D9E75;
          background: rgba(29,158,117,0.1);
          color: #f0f4f2;
        }
        .option-btn.active-purple {
          border-color: #a78bfa;
          background: rgba(167,139,250,0.1);
          color: #f0f4f2;
        }
      `}</style>

      {/* Progress bar */}
      {step > 0 && (
        <div style={{ height: 2, background: "rgba(255,255,255,0.05)", flexShrink: 0 }}>
          <div style={{
            height: "100%",
            background: "linear-gradient(90deg, #1D9E75, #4DC9A0)",
            width: `${progressPct}%`,
            transition: "width 0.6s cubic-bezier(0.16,1,0.3,1)"
          }} />
        </div>
      )}

      {/* Header */}
      {step > 0 && (
        <div style={{
          padding: "16px 32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          flexShrink: 0
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={SummitSubmark} alt="Summit" style={{ height: 80, width: "auto" }} />
          </div>
          <div style={{ fontSize: 12, color: "rgba(240,244,242,0.25)", fontWeight: 300 }}>
            Step {Math.min(step, totalSteps)} of {totalSteps}
          </div>
        </div>
      )}

      {/* ── STEP 0: Cinematic Intro ─────────────────────────────────── */}
      {step === 0 && (
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Background glow */}
          <div style={{
            position: "absolute",
            width: 600,
            height: 600,
            background: "radial-gradient(circle, rgba(29,158,117,0.12) 0%, transparent 70%)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            animation: "glow 4s ease-in-out infinite",
            pointerEvents: "none"
          }} />
          <div style={{
            position: "absolute",
            width: 400,
            height: 400,
            background: "radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)",
            top: "30%",
            left: "60%",
            animation: "glow 6s ease-in-out infinite 1s",
            pointerEvents: "none"
          }} />

          {/* Logo */}
          <div style={{
            opacity: introStage >= 0 ? 1 : 0,
            transform: introStage >= 0 ? "scale(1) translateY(0)" : "scale(0.7) translateY(20px)",
            transition: "all 0.9s cubic-bezier(0.16,1,0.3,1)",
            marginBottom: 32,
            animation: introStage >= 2 ? "float 4s ease-in-out infinite" : "none"
          }}>
            <img src={SummitSubmark} alt="Summit" style={{ height: 140, width: "auto" }} />
          </div>

          {/* Title */}
          <div style={{
            opacity: introStage >= 1 ? 1 : 0,
            transform: introStage >= 1 ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.7s cubic-bezier(0.16,1,0.3,1) 0.1s",
            textAlign: "center",
            marginBottom: 16
          }}>
            <div style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: "clamp(40px, 7vw, 72px)",
              letterSpacing: -2,
              lineHeight: 1.05,
              color: "#f0f4f2"
            }}>
              Reach your grade.
            </div>
          </div>

          {/* Tagline */}
          <div style={{
            opacity: introStage >= 2 ? 1 : 0,
            transform: introStage >= 2 ? "translateY(0)" : "translateY(16px)",
            transition: "all 0.6s cubic-bezier(0.16,1,0.3,1) 0.05s",
            textAlign: "center",
            marginBottom: 52
          }}>
            <div style={{
              fontSize: 17,
              color: "rgba(240,244,242,0.4)",
              fontWeight: 300,
              lineHeight: 1.7,
              maxWidth: 420
            }}>
              The AI study tool built for Scottish students.<br />
              SQA-trained. Mark-scheme aware. Always available.
            </div>
          </div>

          {/* CTA */}
          <div style={{
            opacity: introStage >= 3 ? 1 : 0,
            transform: introStage >= 3 ? "translateY(0)" : "translateY(12px)",
            transition: "all 0.5s cubic-bezier(0.16,1,0.3,1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14
          }}>
            <button
              onClick={() => goNext(1)}
              style={{
                padding: "16px 52px",
                background: "#1D9E75",
                color: "white",
                border: "none",
                borderRadius: 14,
                fontSize: 17,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: -0.3,
                transition: "all 0.2s",
              }}
              onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(29,158,117,0.35)"; }}
              onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            >
              Get started →
            </button>
            <div style={{ fontSize: 12, color: "rgba(240,244,242,0.2)", fontWeight: 300 }}>
              Free to start · Takes 2 minutes · No card required
            </div>
          </div>
        </div>
      )}

      {/* ── STEPS 1–8 ──────────────────────────────────────────────── */}
      {step > 0 && (
        <div style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 24px",
          overflowY: "auto"
        }}>
          <div style={{ width: "100%", maxWidth: 580 }}>
            <SlideStep key={stepKey} direction={direction}>

              {/* ── STEP 1: Name ─────────────────────────────────────── */}
              {step === 1 && (
                <div>
                  <div className="step-label">Step 1 of 8</div>
                  <div className="step-heading">What should we<br />call you?</div>
                  <div className="step-sub">Summit will personalise everything to you from the moment you start.</div>
                  <input
                    className="fancy-input"
                    type="text"
                    placeholder="Your first name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && name.trim() && goNext(2)}
                    autoFocus
                    style={{ marginBottom: 24, fontSize: 22 }}
                  />
                  {name.trim() && (
                    <div style={{
                      padding: "14px 18px",
                      background: "rgba(29,158,117,0.06)",
                      border: "1px solid rgba(29,158,117,0.2)",
                      borderRadius: 10,
                      fontSize: 14,
                      color: "#4DC9A0",
                      fontWeight: 300,
                      marginBottom: 24,
                      animation: "fadeUp 0.4s ease"
                    }}>
                      Good to meet you, <strong style={{ fontWeight: 600 }}>{name.split(" ")[0]}</strong>. Let's build your perfect study plan.
                    </div>
                  )}
                  <button
                    className="btn-primary"
                    onClick={() => goNext(2)}
                    disabled={!name.trim()}
                  >
                    Continue →
                  </button>
                </div>
              )}

              {/* ── STEP 2: Subjects ──────────────────────────────────── */}
              {step === 2 && (
                <div>
                  <div className="step-label">Step 2 of 8</div>
                  <div className="step-heading">
                    {greeting ? `${greeting}, what are` : "What are"}<br />you studying?
                  </div>
                  <div className="step-sub">Select every subject you're sitting this year.</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 28 }}>
                    {SQA_SUBJECTS.map(sub => (
                      <button
                        key={sub}
                        className={`subject-chip${subjects.includes(sub) ? " active" : ""}`}
                        onClick={() => setSubjects(prev =>
                          prev.includes(sub) ? prev.filter(x => x !== sub) : [...prev, sub]
                        )}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                  {subjects.length > 0 && (
                    <div style={{
                      fontSize: 12,
                      color: "#1D9E75",
                      marginBottom: 16,
                      fontWeight: 400,
                      animation: "fadeIn 0.3s ease"
                    }}>
                      {subjects.length} subject{subjects.length !== 1 ? "s" : ""} selected ✓
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 12 }}>
                    <button className="btn-back" onClick={() => goBack(1)}>←</button>
                    <button
                      className="btn-primary"
                      style={{ flex: 1 }}
                      onClick={() => goNext(3)}
                      disabled={subjects.length === 0}
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 3: Levels ────────────────────────────────────── */}
              {step === 3 && (
                <div>
                  <div className="step-label">Step 3 of 8</div>
                  <div className="step-heading">What level are<br />you sitting?</div>
                  <div className="step-sub">Set the level for each of your subjects.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 32 }}>
                    {subjects.map(sub => (
                      <div key={sub} style={{
                        padding: "16px 18px",
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid rgba(255,255,255,0.07)",
                        borderRadius: 12
                      }}>
                        <div style={{ fontSize: 13, color: "rgba(240,244,242,0.55)", marginBottom: 10, fontWeight: 400 }}>{sub}</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {LEVELS.map(l => (
                            <button
                              key={l}
                              onClick={() => setLevels(prev => ({ ...prev, [sub]: l }))}
                              style={{
                                flex: 1,
                                padding: "9px 6px",
                                borderRadius: 8,
                                fontSize: 12,
                                border: levels[sub] === l ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)",
                                background: levels[sub] === l ? "rgba(29,158,117,0.15)" : "transparent",
                                color: levels[sub] === l ? "#4DC9A0" : "rgba(240,244,242,0.4)",
                                cursor: "pointer",
                                fontFamily: "'DM Sans', sans-serif",
                                transition: "all 0.15s"
                              }}
                            >
                              {l}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button className="btn-back" onClick={() => goBack(2)}>←</button>
                    <button
                      className="btn-primary"
                      style={{ flex: 1 }}
                      onClick={() => goNext(4)}
                      disabled={!allLevelsSet}
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 4: Exam date ─────────────────────────────────── */}
              {step === 4 && (
                <div>
                  <div className="step-label">Step 4 of 8</div>
                  <div className="step-heading">When's your<br />first exam?</div>
                  <div className="step-sub">Summit counts down and adjusts your plan as exam day gets closer.</div>
                  <input
                    type="date"
                    value={examDate}
                    onChange={e => setExamDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    max={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
                    style={{
                      width: "100%",
                      padding: "18px 20px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 12,
                      color: "#f0f4f2",
                      fontSize: 18,
                      fontFamily: "'DM Sans', sans-serif",
                      marginBottom: 20,
                      outline: "none",
                      colorScheme: "dark",
                    }}
                  />
                  {daysUntil !== null && daysUntil > 0 && (
                    <div style={{
                      padding: "20px 24px",
                      background: "rgba(29,158,117,0.06)",
                      border: "1px solid rgba(29,158,117,0.2)",
                      borderRadius: 14,
                      marginBottom: 28,
                      display: "flex",
                      alignItems: "center",
                      gap: 18,
                      animation: "scaleIn 0.4s cubic-bezier(0.16,1,0.3,1)"
                    }}>
                      <div style={{
                        fontFamily: "'Instrument Serif', serif",
                        fontSize: 52,
                        color: "#1D9E75",
                        letterSpacing: -2,
                        lineHeight: 1
                      }}>
                        {daysUntil}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, color: "#4DC9A0", fontWeight: 500 }}>days until your first exam</div>
                        <div style={{ fontSize: 12, color: "rgba(240,244,242,0.3)", fontWeight: 300, marginTop: 3 }}>
                          {daysUntil > 30 ? "You have time. Use it well." :
                           daysUntil > 14 ? "Time to focus. Every session counts." :
                           "Final stretch — Summit is with you."}
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 12 }}>
                    <button className="btn-back" onClick={() => goBack(3)}>←</button>
                    <button
                      className="btn-primary"
                      style={{ flex: 1 }}
                      onClick={() => goNext(5)}
                      disabled={!examDate}
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 5: Confidence ────────────────────────────────── */}
              {step === 5 && (
                <div>
                  <div className="step-label">Step 5 of 8</div>
                  <div className="step-heading">How confident<br />are you?</div>
                  <div className="step-sub">Be honest — Summit uses this to prioritise your weakest areas first.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
                    {subjects.map(sub => {
                      const val = confidence[sub];
                      const color = !val ? "rgba(255,255,255,0.15)" : val <= 2 ? "#E24B4A" : val === 3 ? "#EF9F27" : "#1D9E75";
                      const labels = ["", "Not confident", "Struggling", "Getting there", "Fairly confident", "Very confident"];
                      return (
                        <div key={sub} style={{
                          padding: "16px 18px",
                          background: "rgba(255,255,255,0.02)",
                          border: `1px solid ${val ? color + "40" : "rgba(255,255,255,0.07)"}`,
                          borderRadius: 12,
                          transition: "border-color 0.3s"
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                            <div style={{ fontSize: 13, color: "rgba(240,244,242,0.65)" }}>{sub}</div>
                            {val && <div style={{ fontSize: 11, color, fontWeight: 500 }}>{labels[val]}</div>}
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            {[1, 2, 3, 4, 5].map(n => (
                              <button
                                key={n}
                                onClick={() => setConfidence(prev => ({ ...prev, [sub]: n }))}
                                style={{
                                  flex: 1,
                                  height: 38,
                                  borderRadius: 8,
                                  fontSize: 13,
                                  fontWeight: confidence[sub] === n ? 600 : 400,
                                  border: confidence[sub] === n ? `1px solid ${color}` : "1px solid rgba(255,255,255,0.1)",
                                  background: confidence[sub] === n ? `${color}22` : "transparent",
                                  color: confidence[sub] === n ? color : "rgba(240,244,242,0.35)",
                                  cursor: "pointer",
                                  fontFamily: "'DM Sans', sans-serif",
                                  transition: "all 0.15s"
                                }}
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button className="btn-back" onClick={() => goBack(4)}>←</button>
                    <button
                      className="btn-primary"
                      style={{ flex: 1 }}
                      onClick={() => goNext(6)}
                      disabled={!allConfSet}
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 6: Goals + Habits ────────────────────────────── */}
              {step === 6 && (
                <div>
                  <div className="step-label">Step 6 of 8</div>
                  <div className="step-heading">Let's set your<br />targets.</div>
                  <div className="step-sub">Summit will build your study plan around exactly what you want to achieve.</div>

                  {/* Grade target — per subject */}
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ fontSize: 13, color: "rgba(240,244,242,0.5)", marginBottom: 14, fontWeight: 400, letterSpacing: 0.3 }}>What grade are you aiming for in each subject?</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {subjects.map(sub => (
                        <div key={sub} style={{ padding: "14px 16px", background: "rgba(255,255,255,0.02)", border: `1px solid ${gradeTargets[sub] ? "rgba(29,158,117,0.25)" : "rgba(255,255,255,0.07)"}`, borderRadius: 10, transition: "border-color 0.2s" }}>
                          <div style={{ fontSize: 12, color: "rgba(240,244,242,0.5)", marginBottom: 10 }}>{sub} <span style={{ color: "rgba(240,244,242,0.25)", fontWeight: 300 }}>— {levels[sub]}</span></div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {GRADE_TARGETS.map(g => (
                              <button
                                key={g}
                                onClick={() => setGradeTargets(prev => ({ ...prev, [sub]: g }))}
                                style={{
                                  padding: "7px 14px",
                                  borderRadius: 8,
                                  fontSize: 13,
                                  fontWeight: gradeTargets[sub] === g ? 600 : 400,
                                  border: gradeTargets[sub] === g ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)",
                                  background: gradeTargets[sub] === g ? "rgba(29,158,117,0.15)" : "transparent",
                                  color: gradeTargets[sub] === g ? "#4DC9A0" : "rgba(240,244,242,0.4)",
                                  cursor: "pointer",
                                  fontFamily: "'DM Sans', sans-serif",
                                  transition: "all 0.15s"
                                }}
                              >
                                {g}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Study hours */}
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ fontSize: 13, color: "rgba(240,244,242,0.5)", marginBottom: 12, fontWeight: 400, letterSpacing: 0.3 }}>How many hours can you study per week?</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {STUDY_HOURS.map(h => (
                        <button
                          key={h}
                          onClick={() => setStudyHours(h)}
                          style={{
                            flex: 1,
                            padding: "10px 8px",
                            borderRadius: 10,
                            fontSize: 13,
                            fontWeight: studyHours === h ? 600 : 400,
                            border: studyHours === h ? "1px solid #1D9E75" : "1px solid rgba(255,255,255,0.1)",
                            background: studyHours === h ? "rgba(29,158,117,0.15)" : "transparent",
                            color: studyHours === h ? "#4DC9A0" : "rgba(240,244,242,0.45)",
                            cursor: "pointer",
                            fontFamily: "'DM Sans', sans-serif",
                            transition: "all 0.15s"
                          }}
                        >
                          {h}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Biggest struggle */}
                  <div style={{ marginBottom: 32 }}>
                    <div style={{ fontSize: 13, color: "rgba(240,244,242,0.5)", marginBottom: 12, fontWeight: 400, letterSpacing: 0.3 }}>What do you struggle with most?</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {STRUGGLES.map(s => (
                        <button
                          key={s}
                          onClick={() => setBiggestStruggle(s)}
                          className={`option-btn${biggestStruggle === s ? " active" : ""}`}
                        >
                          <div style={{
                            width: 18, height: 18, borderRadius: "50%",
                            border: `1.5px solid ${biggestStruggle === s ? "#1D9E75" : "rgba(255,255,255,0.2)"}`,
                            background: biggestStruggle === s ? "#1D9E75" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0, transition: "all 0.15s"
                          }}>
                            {biggestStruggle === s && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "white" }} />}
                          </div>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 12 }}>
                    <button className="btn-back" onClick={() => goBack(5)}>←</button>
                    <button
                      className="btn-primary"
                      style={{ flex: 1 }}
                      onClick={() => goNext(7)}
                      disabled={subjects.some(s => !gradeTargets[s]) || !studyHours || !biggestStruggle}
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 7: Biggest worry (AI response) ──────────────── */}
              {step === 7 && (
                <div>
                  <div className="step-label">Step 7 of 8</div>
                  <div className="step-heading">
                    {greeting ? `One more thing, ${greeting}.` : "One more thing."}
                  </div>
                  <div className="step-sub">
                    What's your biggest worry about these exams? Summit will respond with something genuinely useful.
                  </div>
                  <textarea
                    value={worry}
                    onChange={e => setWorry(e.target.value)}
                    placeholder={`e.g. I always run out of time in ${subjects[0] || "my exams"} and can never remember the key theories for extended questions...`}
                    rows={4}
                    style={{
                      width: "100%",
                      padding: "18px 20px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 12,
                      color: "#f0f4f2",
                      fontSize: 14,
                      fontFamily: "'DM Sans', sans-serif",
                      resize: "none",
                      lineHeight: 1.65,
                      marginBottom: 16,
                      transition: "border-color 0.2s",
                    }}
                    onFocus={e => e.target.style.borderColor = "rgba(29,158,117,0.5)"}
                    onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                  />
                  {!aiReply && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 8 }}>
                      <button
                        className="btn-primary"
                        onClick={askAI}
                        disabled={!worry.trim() || aiLoading}
                      >
                        {aiLoading ? "Summit is thinking…" : "Ask Summit →"}
                      </button>
                      <div style={{ display: "flex", gap: 12 }}>
                        <button className="btn-back" onClick={() => goBack(6)}>←</button>
                        <button
                          onClick={() => goNext(8)}
                          style={{
                            flex: 1,
                            padding: "14px",
                            background: "transparent",
                            color: "rgba(240,244,242,0.3)",
                            border: "none",
                            borderRadius: 12,
                            fontSize: 14,
                            fontFamily: "'DM Sans', sans-serif",
                            cursor: "pointer"
                          }}
                        >
                          Skip for now →
                        </button>
                      </div>
                    </div>
                  )}
                  {aiReply && (
                    <div style={{ animation: "fadeUp 0.5s ease" }}>
                      <div style={{
                        padding: "18px 20px",
                        background: "rgba(29,158,117,0.07)",
                        border: "1px solid rgba(29,158,117,0.2)",
                        borderRadius: 14,
                        fontSize: 14,
                        color: "#b3e8d8",
                        lineHeight: 1.7,
                        marginBottom: 20,
                        fontWeight: 300,
                      }}>
                        <div style={{ fontSize: 11, color: "#1D9E75", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Summit says</div>
                        {aiReply}
                      </div>
                      <div style={{ display: "flex", gap: 12 }}>
                        <button className="btn-back" onClick={() => goBack(6)}>←</button>
                        <button className="btn-primary" style={{ flex: 1 }} onClick={() => goNext(8)}>
                          Let's go →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP 8: Plan selection ────────────────────────────── */}
              {step === 8 && (
                <div>
                  <div className="step-label">Almost there</div>
                  <div className="step-heading">
                    {greeting ? `${greeting}, choose` : "Choose"}<br />your plan.
                  </div>
                  {daysUntil && daysUntil > 0 && (
                    <div style={{ fontSize: 14, color: "#4DC9A0", marginBottom: 28, fontWeight: 400 }}>
                      Your first exam is in{" "}
                      <span style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2" }}>{daysUntil}</span>{" "}
                      days.
                    </div>
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 20 }}>

                    {/* Free */}
                    <div className="plan-card" style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "rgba(240,244,242,0.3)", marginBottom: 6, fontWeight: 500 }}>Free</div>
                          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 36, color: "#f0f4f2", letterSpacing: -1, lineHeight: 1 }}>£0</div>
                          <div style={{ fontSize: 12, color: "rgba(240,244,242,0.25)", fontWeight: 300, marginTop: 4 }}>No card required</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 20, marginBottom: 18, flexWrap: "wrap" }}>
                        {["10 messages/day", "Concept chat only", "1 subject"].map(f => (
                          <div key={f} style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", display: "flex", gap: 6, alignItems: "center", fontWeight: 300 }}>
                            <span style={{ color: "#1D9E75" }}>✓</span>{f}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={finishFree}
                        style={{
                          padding: "12px",
                          background: "transparent",
                          color: "#f0f4f2",
                          border: "1px solid rgba(255,255,255,0.15)",
                          borderRadius: 10,
                          fontSize: 14,
                          fontFamily: "'DM Sans', sans-serif",
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
                      >
                        Start free
                      </button>
                    </div>

                    {/* Pro */}
                    <div className="plan-card" style={{ border: "1px solid rgba(29,158,117,0.45)", background: "rgba(29,158,117,0.05)" }}>
                      <div style={{ position: "absolute", top: -13, left: 24, background: "#1D9E75", color: "white", fontSize: 11, padding: "3px 14px", borderRadius: 20, fontWeight: 600, letterSpacing: 0.5 }}>
                        ⭐ MOST POPULAR
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#1D9E75", marginBottom: 6, fontWeight: 500 }}>Pro</div>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 36, color: "#f0f4f2", letterSpacing: -1, lineHeight: 1 }}>£6.99<span style={{ fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>/mo</span></div>
                            <span style={{ background: "rgba(239,159,39,0.15)", color: "#EF9F27", fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>40% OFF</span>
                          </div>
                          <div style={{ fontSize: 12, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="original-price" style={{ fontSize: 12, fontWeight: 300 }}>was £11.65</span>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", marginBottom: 18 }}>
                        {["Unlimited messages", "All 4 study modes", "All SQA subjects", "All predicted papers free", "Weakness tracker + planner"].map(f => (
                          <div key={f} style={{ fontSize: 12, color: f.includes("predicted") ? "#EF9F27" : "rgba(240,244,242,0.6)", display: "flex", gap: 6, alignItems: "center", fontWeight: 300 }}>
                            <span style={{ color: f.includes("predicted") ? "#EF9F27" : "#1D9E75" }}>✓</span>{f}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={finishPro}
                        style={{
                          padding: "13px",
                          background: "#1D9E75",
                          color: "white",
                          border: "none",
                          borderRadius: 10,
                          fontSize: 15,
                          fontFamily: "'DM Sans', sans-serif",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                        onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
                        onMouseOut={e => e.currentTarget.style.opacity = "1"}
                      >
                        Claim exam discount →
                      </button>
                      <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", textAlign: "center", marginTop: 10, fontWeight: 300 }}>Cancel any time · Only 53 discounted places left</div>
                    </div>

                    {/* Coach Boost add-on */}
                    <div className="plan-card" style={{ border: "1px solid rgba(167,139,250,0.45)", background: "rgba(167,139,250,0.05)" }}>
                      <div style={{ position: "absolute", top: -13, left: 24, background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "white", fontSize: 11, padding: "3px 14px", borderRadius: 20, fontWeight: 600, letterSpacing: 0.5 }}>
                        ✦ AI COACH
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#a78bfa", marginBottom: 6, fontWeight: 500 }}>Pro + AI Coach</div>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 36, color: "#f0f4f2", letterSpacing: -1, lineHeight: 1 }}>£6.99<span style={{ fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>/mo</span></div>
                          </div>
                          <div style={{ fontSize: 12, color: "#a78bfa", fontWeight: 400, marginTop: 4 }}>+ £7.99 one-off for the coach</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", marginBottom: 18 }}>
                        {[
                          ["Everything in Pro", false],
                          ["AI coach — knows your weak spots", true],
                          ["Progress dashboard", true],
                          ["Day-by-day revision schedule", true],
                        ].map(([f, highlight]) => (
                          <div key={f} style={{ fontSize: 12, color: highlight ? "#a78bfa" : "rgba(240,244,242,0.6)", display: "flex", gap: 6, alignItems: "center", fontWeight: highlight ? 500 : 300 }}>
                            <span style={{ color: highlight ? "#a78bfa" : "#1D9E75" }}>✓</span>{f}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={finishBoost}
                        style={{
                          padding: "13px",
                          background: "linear-gradient(135deg, #a78bfa, #7c3aed)",
                          color: "white",
                          border: "none",
                          borderRadius: 10,
                          fontSize: 15,
                          fontFamily: "'DM Sans', sans-serif",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                        onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
                        onMouseOut={e => e.currentTarget.style.opacity = "1"}
                      >
                        Get Pro + AI Coach →
                      </button>
                      <div style={{ fontSize: 11, color: "rgba(240,244,242,0.2)", textAlign: "center", marginTop: 10, fontWeight: 300 }}>Pro subscription + £7.99 one-off · cancel Pro any time</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(240,244,242,0.15)", textAlign: "center", fontWeight: 300 }}>
                    All plans · cancel any time · no hidden fees
                  </div>
                </div>
              )}

              {/* Step 8b: Free subject picker */}
              {step === "8b" && (
                <div>
                  <div className="step-label">One last thing</div>
                  <div className="step-heading">Which subject<br />first?</div>
                  <div className="step-sub">
                    Free plan includes one subject. Pick the one you need most — or upgrade to unlock all {subjects.length}.
                  </div>

                  <div style={{
                    padding: "14px 18px",
                    background: "rgba(29,158,117,0.07)",
                    border: "1px solid rgba(29,158,117,0.25)",
                    borderRadius: 12,
                    marginBottom: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#4DC9A0" }}>
                        Unlock all {subjects.length} subjects
                      </div>
                      <div style={{ fontSize: 11, color: "rgba(240,244,242,0.4)", fontWeight: 300, marginTop: 2 }}>
                        Pro — £6.99/mo · unlimited messages · all modes
                      </div>
                    </div>
                    <button
                      onClick={finishPro}
                      style={{
                        padding: "9px 18px",
                        background: "#1D9E75",
                        color: "white",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 13,
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        flexShrink: 0
                      }}
                    >
                      Upgrade →
                    </button>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
                    {subjects.map(function(sub) {
                      return (
                        <button
                          key={sub}
                          onClick={function() { setChosenFreeSubject(sub); }}
                          className={"option-btn" + (chosenFreeSubject === sub ? " active" : "")}
                        >
                          <div style={{
                            width: 18, height: 18, borderRadius: "50%",
                            border: "1.5px solid " + (chosenFreeSubject === sub ? "#1D9E75" : "rgba(255,255,255,0.2)"),
                            background: chosenFreeSubject === sub ? "#1D9E75" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0, transition: "all 0.15s"
                          }}>
                            {chosenFreeSubject === sub && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "white" }} />}
                          </div>
                          <div style={{ flex: 1, textAlign: "left" }}>
                            <div style={{ fontSize: 14 }}>{sub}</div>
                            <div style={{ fontSize: 11, color: "rgba(240,244,242,0.3)", fontWeight: 300 }}>{levels[sub]}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ display: "flex", gap: 12 }}>
                    <button className="btn-back" onClick={function() { goBack(8); }}>←</button>
                    <button
                      className="btn-primary"
                      style={{ flex: 1 }}
                      onClick={finishFree}
                      disabled={!chosenFreeSubject}
                    >
                      Start with {chosenFreeSubject ? chosenFreeSubject.split(" ")[0] : "one subject"} →
                    </button>
                  </div>
                </div>
              )}

            </SlideStep>
          </div>
        </div>
      )}
    </div>
  );
}